import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, OnDestroy, OutputRefSubscription, createComponent, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfirmationDialogComponent, ConfirmationOptions } from './confirmation-dialog.component';

@Injectable({ providedIn: 'root' })
export class ConfirmationService implements OnDestroy {
  private readonly application = inject(ApplicationRef);
  private readonly environment = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);
  private dismiss: (() => void) | null = null;

  confirm(options: ConfirmationOptions): Observable<boolean> {
    return new Observable(subscriber => {
      if (this.dismiss) {
        subscriber.next(false);
        subscriber.complete();
        return;
      }

      const previousFocus = this.document.activeElement;
      let component: ComponentRef<ConfirmationDialogComponent> | undefined;
      let host: HTMLElement | undefined;
      let decision: OutputRefSubscription | undefined;
      let attached = false;
      let settled = false;
      const cleanup = () => {
        if (settled) return;
        settled = true;
        this.dismiss = null;
        decision?.unsubscribe();
        component?.instance.close();
        if (attached) this.application.detachView(component!.hostView);
        component?.destroy();
        host?.remove();
        if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
      };
      const finish = (confirmed: boolean) => {
        cleanup();
        subscriber.next(confirmed);
        subscriber.complete();
      };
      this.dismiss = () => finish(false);

      try {
        component = createComponent(ConfirmationDialogComponent, { environmentInjector: this.environment });
        host = component.location.nativeElement as HTMLElement;
        component.setInput('options', { ...options });
        this.application.attachView(component.hostView);
        attached = true;
        this.document.body.appendChild(host);
        decision = component.instance.decided.subscribe(finish);
        component.changeDetectorRef.detectChanges();
        component.instance.open();
      } catch (error) {
        cleanup();
        subscriber.error(error);
      }

      return cleanup;
    });
  }

  ngOnDestroy(): void {
    this.dismiss?.();
  }
}
